import { TableCell } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import React, { useMemo, useState } from 'react'

/**
 * Table heading cell that sticks to the top of the WINDOW as the page scrolls
 * ("sticky freeze") and IS the search box: one row, the column name shown in
 * grey inside the box (Satya, 23 Oct — never a label row plus a box row,
 * it wastes space). Pair it with
 * useColumnSearch. Sticking only works when no ancestor of the table has
 * `overflow` set, so the table must fit the page width (cells wrap) rather
 * than sit in a sideways-scroll box.
 */
const useStyles = makeStyles((theme) => ({
  cell: {
    position: 'sticky',
    top: 0,
    zIndex: 3,
    background: theme.palette.background.paper,
    verticalAlign: 'top',
    // The bottom border scrolls away with a sticky cell; a shadow stays.
    boxShadow: `inset 0 -1px 0 ${theme.palette.divider}`
  },
  input: {
    display: 'block',
    width: '100%',
    minWidth: 60,
    boxSizing: 'border-box',
    font: 'inherit',
    fontSize: 13,
    fontWeight: 500,
    color: theme.palette.text.primary,
    '&::placeholder': { color: theme.palette.text.secondary, opacity: 1 },
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    padding: '3px 6px',
    background: theme.palette.background.paper,
    '&:focus': { outline: 'none', borderColor: theme.palette.primary.main }
  },
  active: { borderColor: theme.palette.primary.main }
}))

// `search` comes from useColumnSearch's box(key); leave it out for a column
// with nothing to search (actions).
export const SearchHeadCell = ({ label, search, align, style }) => {
  const classes = useStyles()
  return (
    <TableCell align={align} className={classes.cell} style={style}>
      {search ? (
        <input
          className={`${classes.input} ${search.value ? classes.active : ''}`}
          placeholder={label}
          aria-label={`Search ${label}`}
          title={label}
          value={search.value}
          onChange={(e) => search.onChange(e.target.value)}
          style={align === 'right' ? { textAlign: 'right' } : undefined}
        />
      ) : (
        label
      )}
    </TableCell>
  )
}

const text = (v) => (v === null || v === undefined ? '' : String(v))

/**
 * Per-column search. `columns` maps a key to the text a row shows in that
 * column (define it outside the component so it stays the same object).
 * Returns the matching rows (every filled box must match, ignoring case),
 * box(key) for SearchHeadCell, and whether any box is filled.
 */
export const useColumnSearch = (rows, columns) => {
  const [search, setSearch] = useState({})
  const filled = Object.keys(search).filter((k) => search[k].trim())
  const matching = useMemo(() => {
    const keys = Object.keys(search).filter((k) => search[k].trim())
    if (!keys.length) return rows
    return rows.filter((r) =>
      keys.every((k) =>
        text(columns[k](r))
          .toLowerCase()
          .includes(search[k].trim().toLowerCase())
      )
    )
  }, [rows, columns, search])
  const box = (key) => ({
    value: search[key] || '',
    onChange: (v) => setSearch((s) => ({ ...s, [key]: v }))
  })
  return { rows: matching, box, searching: filled.length > 0, search }
}
